import { useEffect, useRef, useState } from 'react';
import useOrdersExport from '@/hooks/useOrdersExport';
import { selectIsExportOpen, useExportModalStore } from '@/stores/useExportModalStore';
import { exportDelivery } from '@/utils/helpers/orderExport';

/** What the modal shows after an export: downloaded, queued, or failed. */
export type ExportPhase = 'idle' | 'done' | 'queued' | 'failed';

/** How long "Downloaded ✓" stays on screen before the modal goes away. */
export const EXPORT_DONE_CLOSE_MS = 1200;

const PHONE_QUERY = '(max-width: 639px)';

const useIsPhone = () => {
  const [isPhone, setIsPhone] = useState(() => window.matchMedia(PHONE_QUERY).matches);

  useEffect(() => {
    const mql = window.matchMedia(PHONE_QUERY);
    const onChange = () => setIsPhone(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isPhone;
};

/**
 * BOXY-409 — presentation state around useOrdersExport (which stays the
 * source of truth): the after-export phase, keeping the modal on screen for
 * "Downloaded ✓" / "We'll notify you" after the store has closed it, and
 * dialog vs bottom sheet.
 */
const useExportModalView = () => {
  const live = useOrdersExport();
  const [phase, setPhase] = useState<ExportPhase>('idle');
  const isPhone = useIsPhone();

  // Closed queries count nothing; keep showing what was exported while the
  // success state is on screen and the modal animates out.
  const snapshot = useRef(live);
  if (live.isOpen) snapshot.current = live;
  const state = live.isOpen ? live : snapshot.current;

  useEffect(() => {
    if (live.isOpen) setPhase('idle');
  }, [live.isOpen]);

  useEffect(() => {
    if (phase !== 'done') return undefined;
    const id = setTimeout(() => setPhase('idle'), EXPORT_DONE_CLOSE_MS);
    return () => clearTimeout(id);
  }, [phase]);

  const exportNow = async () => {
    if (!live.canExport || live.count === undefined) return;
    const delivery = exportDelivery(live.count);
    setPhase('idle');
    await live.submit();
    // submit closes the store on success and leaves it open on failure.
    const closed = !selectIsExportOpen(useExportModalStore.getState());
    setPhase(closed ? (delivery === 'async' ? 'queued' : 'done') : 'failed');
  };

  const dismiss = () => {
    live.close();
    setPhase('idle');
  };

  return {
    state,
    phase,
    isPhone,
    open: live.isOpen || phase === 'done' || phase === 'queued',
    busy: live.isExporting || phase === 'done' || phase === 'queued',
    exportNow,
    dismiss,
  };
};

export type ExportModalView = ReturnType<typeof useExportModalView>;

export default useExportModalView;
